import { Component, ElementRef, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { 
  EMPTY, 
  Subject, 
  BehaviorSubject,
  combineLatest,
  debounceTime, 
  distinctUntilChanged,
  exhaustMap, 
  catchError, 
  takeUntil, 
  tap,
  map,
  startWith
} from 'rxjs';
import { ConfigEntityService } from 'src/app/core/services/config-entity.service';
import { LogService } from 'src/app/core/services/log-hub.service';
import { NavigationService } from '../../../../core/services/navigation.service';

@Component({
  selector: 'app-log-viewer',
  templateUrl: './log-viewer.component.html',
  styleUrls: ['./log-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LogViewerComponent implements OnInit, OnDestroy {
  @ViewChild('logContent') logContentElement: ElementRef;

  private destroy$ = new Subject<void>();
  public searchTerm$ = new BehaviorSubject<string>('');
  public isMouseOver$ = new BehaviorSubject<boolean>(false);
  public isAtBottom$ = new BehaviorSubject<boolean>(true);
  public downloadSyncHPCLogs$ = new Subject<void>();
  private readonly SCROLL_THRESHOLD = 50; 

  // Reactive streams
  filteredLogs$ = combineLatest([
    this.logService.logs$,
    this.searchTerm$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    )
  ]).pipe(
    map(([logs, searchTerm]) => this.filterLogs(logs, searchTerm)),
    takeUntil(this.destroy$)
  );

  constructor(
    private logService: LogService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private configEntityService: ConfigEntityService,
    private snackBar: MatSnackBar,
    private navService: NavigationService,
  ) {}

  ngOnInit(): void {
    this.setupNavigation();
    this.logService.startConnection();
    this.setupAutoScroll();
    this.setupDownload();
  }

  ngOnDestroy(): void {
    this.logService.stopConnection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm$.next(searchTerm);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const isAtBottom = this.checkIfAtBottom(element);
    this.isAtBottom$.next(isAtBottom);
  }

  onMouseEnter(): void {
    this.isMouseOver$.next(true);
  }

  onMouseLeave(): void {
    this.isMouseOver$.next(false);
  }

  onLogsDownload(): void {
    this.downloadSyncHPCLogs$.next();
  }

  highlightSearchTerm(line: string): SafeHtml {
    const searchTerm = this.searchTerm$.value;
    if (!searchTerm) return line;

    const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const highlighted = line.replace(regex, match => `<span class="highlight">${match}</span>`);
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private setupNavigation(): void {
    this.navService.sendLeftPanelIndex(13);
    this.configEntityService.sendActiveEntity('SyncHPC Logs');
    this.navService.sendChildData('SyncHPC Logs');
    this.navService.sendParentData('');
  }

  private filterLogs(logs: string[], searchTerm: string): string[] {
    if (!searchTerm) return logs;
    
    const searchLower = searchTerm.toLowerCase();
    return logs.filter(log => log.toLowerCase().includes(searchLower));
  }

  private setupAutoScroll(): void {
    combineLatest([
      this.filteredLogs$,
      this.isAtBottom$,
      this.searchTerm$.pipe(map(term => !term)) // Only auto-scroll when not searching
    ]).pipe(
      debounceTime(50), 
      takeUntil(this.destroy$)
    ).subscribe(([logs, isAtBottom, shouldAutoScroll]) => {
      if (shouldAutoScroll && isAtBottom && logs.length > 0) {
        this.scrollToBottom();
      }
    });
  }

  private setupDownload(): void {
    this.downloadSyncHPCLogs$.pipe(
      tap(() => {
        this.snackBar.open('Please wait, download is in progress.', 'Ok', {
          duration: 3000
        });
      }),
      exhaustMap(() => this.configEntityService.downloadSyncHPCLogs()),
      catchError((error) => {
        this.snackBar.open('No file found', 'Ok', { duration: 5000 });
        console.error('Error downloading logs', error);
        return EMPTY;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data: any) => {
        if (data) {
          this.downloadFile('SyncHPCLogs.txt', data);
        }
        this.snackBar.dismiss();
      }
    });
  }

  private scrollToBottom(): void {
    if (this.logContentElement?.nativeElement) {
      const element = this.logContentElement.nativeElement;
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'smooth' 
      });
      // Update the isAtBottom state after scrolling
      this.isAtBottom$.next(true);
    }
  }

  private checkIfAtBottom(element: HTMLElement): boolean {
    const threshold = this.SCROLL_THRESHOLD;
    const isAtBottom = element.scrollTop >= (element.scrollHeight - element.clientHeight - threshold);
    return isAtBottom;
  }

  private downloadFile(filename: string, fileData: any): void {
    const cleanFilename = filename.replace(/['"]+/g, '');
    const url = window.URL.createObjectURL(fileData);
    const anchor = document.createElement('a');
    
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = cleanFilename;
    
    document.body.appendChild(anchor);
    anchor.click();
    
    window.URL.revokeObjectURL(url);
    document.body.removeChild(anchor);
  }
}